import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Wrench } from "lucide-react";
import { PhotoMulti } from "@/components/PhotoCapture";

const ST = {
  received: "受付", checking: "内容確認", estimate: "見積", approved: "承認", ordered: "発注",
  in_progress: "工事中", completed: "完了", paid: "対応済",
};
const URG = { low: "低", medium: "中", high: "高" };

export default function TenantRepairs() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const [urgency, setUrgency] = useState("medium");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tenant/repairs").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    try {
      await api.post("/tenant/repairs", { content, urgency, photos });
      toast.success("修繕依頼を送信しました");
      setContent(""); setUrgency("medium"); setPhotos([]); setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (rows === null) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  return (
    <div data-testid="tenant-repairs">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-light">修繕・不具合</h1>
        <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2 rounded-md" data-testid="repair-new-btn">
          <Plus className="w-4 h-4" />不具合を依頼
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="bg-[#161616] border border-white/5 rounded-lg p-5 mb-6 space-y-4" data-testid="repair-form">
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">不具合の内容</label>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={3} placeholder="例）キッチンの水漏れ、給湯器が動かない など"
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" data-testid="repair-content" />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">緊急度</label>
            <div className="flex gap-2">
              {Object.entries(URG).map(([k, v]) => (
                <button type="button" key={k} onClick={() => setUrgency(k)} data-testid={`repair-urgency-${k}`}
                  className={`px-3 py-1.5 text-sm rounded-md border ${urgency === k ? "border-brand bg-brand/10 text-white" : "border-white/10 text-gray-400"}`}>{v}</button>
              ))}
            </div>
          </div>
          <PhotoMulti label="写真（任意）" values={photos} onChange={setPhotos} testid="repair-photos" />
          <button type="submit" disabled={busy} className="bg-brand hover:bg-brand-hover text-white text-sm px-5 py-2 rounded-md inline-flex items-center gap-2" data-testid="repair-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}送信する
          </button>
        </form>
      )}

      {rows.length === 0 ? (
        <div className="text-gray-500 text-sm py-10 text-center bg-[#161616] rounded-lg">修繕依頼はまだありません。</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="bg-[#161616] border border-white/5 rounded-lg p-4 flex items-start justify-between gap-4" data-testid={`repair-row-${r.id}`}>
              <div>
                <div className="text-sm">{r.content}</div>
                <div className="text-xs text-gray-500 mt-1">緊急度: {URG[r.urgency] || r.urgency} ・ {(r.created_at || "").slice(0, 10)}</div>
                {(() => {
                  const imgs = (r.photos && r.photos.length) ? r.photos : (r.photo ? [r.photo] : []);
                  return imgs.length > 0 ? (
                    <div className="flex gap-2 mt-2 flex-wrap" data-testid={`repair-photos-${r.id}`}>
                      {imgs.map((p, idx) => (
                        <img key={idx} src={`${API}/tenant/files/${p}`} alt="" className="w-16 h-16 object-cover rounded-md border border-white/10" />
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-brand/15 text-brand whitespace-nowrap">{ST[r.status] || r.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
